import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

import { callingBehaviorConfigAPI } from '../../services/api';
import { Button } from '../ui/button';

// 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md 3.4): the
// dashboard side of call-center alerts — provider failures, failed calls,
// slow replies (backend app/services/calling/call_alerts.py). Polls every
// minute; renders nothing when there's nothing wrong.

type CallAlert = {
  id: string;
  kind: string;
  title: string;
  message: string;
  created_at: string;
};

const POLL_MS = 60_000;

export default function CallingAlertsBanner() {
  const [alerts, setAlerts] = useState<CallAlert[]>([]);

  const load = useCallback(() => {
    callingBehaviorConfigAPI
      .getAlerts()
      .then((data: any) => setAlerts(Array.isArray(data?.alerts) ? data.alerts : []))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const dismiss = async (id: string) => {
    setAlerts((current) => current.filter((a) => a.id !== id));
    await callingBehaviorConfigAPI.dismissAlert(id).catch(() => undefined);
  };

  if (!alerts.length) return null;

  return (
    <div className="space-y-2">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{alert.title}</p>
            <p className="text-xs text-muted-foreground">
              {alert.message} · {new Date(alert.created_at).toLocaleString()}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 shrink-0 p-0"
            onClick={() => dismiss(alert.id)}
            aria-label="Dismiss alert"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}
